/* ============================================================
   LockedFeature — verrouillage d'une zone selon le plan
   ------------------------------------------------------------
   Chantier « palier Découverte 9,99 € » (30/07/2026).

   Usage :
     <LockedFeature capability="ai_reply" featureKey="repondre">
       <TabRepondreContainer />
     </LockedFeature>

   Comportement :
     - capacité présente (ou pas encore chargée) → rend les enfants tels quels
     - capacité absente → enfants floutés + inertes, overlay avec CTA
       « Passer à Pro » vers /abonnement

   ⚠️ L'onglet n'est JAMAIS retiré de la navigation : un onglet visible mais
   verrouillé convertit, un onglet absent ne vend rien.

   ⚠️ Ceci est du confort d'UI, pas une sécurité : chaque route sensible
   renvoie un 403 côté serveur (backend/middleware/requireCapability.js).
   ============================================================ */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCapabilities from '../../hooks/useCapabilities';
import styles from './LockedFeature.module.css';

export default function LockedFeature({
  capability,
  featureKey,
  children,
}) {
  const { has, loading, userPlan } = useCapabilities();
  const navigate = useNavigate();
  const { t } = useTranslation('tabs');

  // Pendant le chargement, `has()` renvoie true → aucun cadenas ne clignote
  // devant un abonné Pro. Le backend reste la barrière réelle.
  if (!capability || loading || has(capability)) {
    return <>{children}</>;
  }

  // Titre/texte spécifiques à la fonctionnalité si la clé existe,
  // sinon libellé générique.
  const title = t(`locked.features.${featureKey}.title`, {
    defaultValue: t('locked.generic_title'),
  });
  const text = t(`locked.features.${featureKey}.text`, {
    defaultValue: t('locked.generic_text'),
  });

  return (
    <div className={styles.wrap}>
      {/* inert : booléen natif en React 19 — retire tout le sous-arbre du
          focus clavier et de l'arbre d'accessibilité (le CSS pointer-events
          ne bloquerait que la souris). */}
      <div className={styles.blurred} aria-hidden="true" inert={true}>
        {children}
      </div>

      <div className={styles.overlay} role="region" aria-label={title}>
        <div className={styles.card}>
          <div className={styles.icon} aria-hidden="true">🔒</div>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.text}>{text}</p>
          <button
            type="button"
            className={styles.cta}
            onClick={() => navigate('/abonnement')}
          >
            {t('locked.cta')}
          </button>
          {userPlan && (
            <span className={styles.plan}>
              {t('locked.current_plan', {
                plan: t(`abonnement.plans.${userPlan}.label`, { defaultValue: userPlan }),
              })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
